import React, { ReactElement, useEffect } from "react";
import { Button } from "rizzui/button";
import { Text } from "rizzui/typography";
import { useModal } from "../hooks/useModal";
import { useNavigate } from "react-router";
import HubspotIcon from "../assets/hubspot";
import CSVIcon from "../assets/csv";
import GoogleSheetIcon from "../assets/googleSheet";
import cn from "../lib/utils/class-name";
import CheckIcon from "../assets/checkIcon";
import ImportFile from "./cards/importFile";
import ConsentModal from "./consentModal";

type Integration = {
  title: string;
  subtitle: string;
  buttonFunction: () => void;
  icon: ReactElement;
  id: string;
};

type contactType = "hubspot" | "google-sheet" | "csv-upload";

export default function ImportContacts() {
  const { openModal, closeModal, isOpen } = useModal();
  const navigate = useNavigate();
  const [selected, setSeletced] = React.useState<string | null>(null);
  const [done, setDone] = React.useState<contactType[]>([]);
  const [loading, setLoading] = React.useState<contactType[]>([]);

  useEffect(() => {
    isOpen === false && setSeletced(null);
  }, [isOpen]);

  const handleClick = () => {
    if (!selected) return;
    integrations
      .find((integration) => integration.id === selected)
      ?.buttonFunction();
  };

  const integrations: Integration[] = [
    {
      id: "hubspot",
      title: "HubSpot CRM",
      subtitle: "Sync contacts directly from your CRM",
      buttonFunction: () => {
        openModal({ view: <ConsentModal type="hubspot" /> });
      },
      icon: <HubspotIcon className={"size-[32px]"} />,
    },

    {
      id: "google-sheet",
      title: "Google sheet",
      subtitle: "Connect a live spreadsheet of contacts",
      buttonFunction: () => {
        openModal({ view: <ConsentModal type="google_drive" /> });
      },
      icon: <GoogleSheetIcon className={"size-[32px]"} />,
    },

    {
      id: "csv-upload",
      title: "CSV Upload",
      subtitle: "Manually import a file from your device",
      buttonFunction: () =>
        openModal({ view: <ImportFile page="contacts" type="csv" /> }),
      icon: <CSVIcon className={"text-[#758599]"} />,
    },
  ];

  return (
    <div className="md:w-[503px] w-[96%] bg-background dark:bg-[#2A2C30] rounded-[8px] p-[32px] mx-auto">
      <button
        className="absolute top-4 right-4 p-1 rounded-lg z-10 text-gray-400 bg-transparent hover:text-gray-600 dark:hover:text-gray-300"
        onClick={closeModal}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="size-[18px]"
        >
          <path
            fillRule="evenodd"
            d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      <div className="flex flex-col gap-[40px]">
        <div className="flex flex-col gap-[8px]">
          <Text className="text-primary-dark font-ibmPlexSerif font-[600] text-[18px] leading-[23.4px]">
            Import your contact
          </Text>

          <Text className="text-[#758599] font-switzer text-[14px]">
            Choose where to import your contacts from
          </Text>
        </div>

        <div className="flex flex-col gap-[14px]">
          {integrations.map((integration, index) => (
            <div
              onClick={() => setSeletced(integration.id)}
              key={index}
              className={cn(
                "cursor-pointer border border-muted rounded-[8px] flex items-center gap-[16px] h-[72px] pl-[24px] relative",
                selected === integration.id && "border-primary-dark"
              )}
            >
              {selected === integration.id && (
                <div className="size-[16px] flex justify-center items-center !bg-btn rounded-full absolute top-[8px] right-[8px]">
                  <CheckIcon className={"text-background"} />
                </div>
              )}
              <div>{integration.icon}</div>

              <div className="flex flex-col gap-[8px] font-switzer">
                <Text className="text-primary-dark font-[500] text-[14px]">
                  {integration.title}
                </Text>
                <Text className="text-[#758599] font-[400] text-[12px]">
                  {integration.subtitle}
                </Text>
              </div>
            </div>
          ))}
        </div>

        <Button
          className="!bg-btn w-full rounded-[4px] h-[40px] disabled:!bg-[#758599]"
          disabled={!selected}
          onClick={handleClick}
        >
          <Text className="font-switzer text-[12px] font-[500] text-background">
            Continue
          </Text>
        </Button>
      </div>
    </div>
  );
}
